import { Chip, Typography } from "@mui/material";
import {
  formatDateDayjs,
  ASTableV2,
  ASTableV2Body,
  ASTableV2Cell,
  ASTableV2Header,
  ASTableV2Row,
  ASTableV2RowEmpty
} from "@qubit/autoparts";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { Inv_InventoryDateLabelSettings } from "~/config/clientConfig";

import useFlag from "~/config/flags";
import NotesRow from "~/features/autostorePutaway/NotesRow";
import { useGetPutawayTasks } from "~/features/autostorePutaway/useGetPutawayTasks";
import { checkIsExpiration } from "~/lib/helpers";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import { PutAwayTaskSummaryDto } from "~/types/api";

import { AutostorePutawayOldSearch } from "./AutostorePutawayOldSearch";
import { setSearchData } from "./autostorePutawayOld.slice";

type Props = {
  inv_inventoryDateLabel: Inv_InventoryDateLabelSettings;
  handleSelectRow: (row: PutAwayTaskSummaryDto) => void;
  pageLimit: 5 | 7 | 10;
  selectedRow?: PutAwayTaskSummaryDto | undefined;
};

export const PutawayTasksTableOld = (props: Props) => {
  const hasPutawayTableV2EnhancedFlag =
    useFlag().tablesV2Enhanced.includes("inductionTableV2");

  const { inv_inventoryDateLabel, pageLimit, selectedRow, handleSelectRow } =
    props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { putaway_showScanFilters } = useAppSelector(selectServerConfig);

  const searchData = useAppSelector(
    (state) => state.autostorePutawayOld.searchData
  );

  const {
    putawayTasks,
    putawayTasksLoading,
    putawayTasksTotalCount,
    error: putawayTasksError
  } = useGetPutawayTasks(pageLimit, searchData);

  const totalPageCount = putawayTasksTotalCount
    ? Math.ceil(putawayTasksTotalCount / pageLimit)
    : 0;

  const tableHeaderColNames = [
    t("product"),
    t("orderedQuantityAbbr"),
    t("remainingQuantityAbbr"),
    t("date"),
    t("purchase order number abbr"),
    t("decant rate"),
    t("notes")
  ];

  const removeFilterCb = (barcode: string) => {
    dispatch(
      setSearchData({
        ...searchData,
        scannedBarcodes: searchData.scannedBarcodes.filter(
          (bc) => bc !== barcode
        )
      })
    );
  };

  const showFilters =
    putaway_showScanFilters && !!searchData.scannedBarcodes.length;

  return (
    <ASTableV2
      gridTemplateColumns={`1fr repeat(${tableHeaderColNames.length - 1}, auto)`}
      gridTemplateRows={`${showFilters ? "50px 50px" : "50px"} repeat(${pageLimit}, 1fr)`}
      isLoading={putawayTasksLoading}
      pagination={{
        page: searchData.offset,
        pageCount: putawayTasksError ? 1 : totalPageCount,
        setPage: (p) => dispatch(setSearchData({ ...searchData, offset: p }))
      }}
      searchComponent={
        hasPutawayTableV2EnhancedFlag ? (
          <AutostorePutawayOldSearch />
        ) : undefined
      }
    >
      <ASTableV2Header>
        <ASTableV2Row>
          {tableHeaderColNames.map((h) => (
            <ASTableV2Cell key={h}>
              <Typography variant="tableBody">{h}</Typography>
            </ASTableV2Cell>
          ))}
        </ASTableV2Row>
        {showFilters && (
          <ASTableV2Row>
            <ASTableV2Cell sx={{ gridColumn: "1 / -1" }}>
              {searchData.scannedBarcodes.map((filter) => (
                <Chip
                  key={filter}
                  label={filter}
                  color="1"
                  onDelete={
                    removeFilterCb ? () => removeFilterCb(filter) : undefined
                  }
                  sx={{ mr: 2, fontSize: 14 }}
                />
              ))}
            </ASTableV2Cell>
          </ASTableV2Row>
        )}
      </ASTableV2Header>
      <ASTableV2Body>
        {!putawayTasksLoading && !putawayTasks.length && (
          <ASTableV2RowEmpty gridRow={`${showFilters ? 3 : 2} / last-line`}>
            {t("no results")}
          </ASTableV2RowEmpty>
        )}
        {!putawayTasksError &&
          !putawayTasksLoading &&
          putawayTasks.map((task) => (
            <ASTableV2Row
              key={task.putAwayTaskId}
              onClick={() => handleSelectRow(task)}
              selected={task.putAwayTaskId === selectedRow?.putAwayTaskId}
              highlighted={task.putAwayTaskId === selectedRow?.putAwayTaskId}
            >
              <ASTableV2Cell>
                <Typography variant="tableBody">
                  {task.product.productName}
                </Typography>
              </ASTableV2Cell>
              <ASTableV2Cell>
                <Typography variant="tableBody">
                  {task.orderedQuantity.value.toString()}{" "}
                  {task.orderedQuantity.units}
                </Typography>
              </ASTableV2Cell>
              <ASTableV2Cell>
                <Typography variant="tableBody">
                  {task.remaining.value.toString()} {task.remaining.units}
                </Typography>
              </ASTableV2Cell>
              <ASTableV2Cell>
                <Typography variant="tableBody">
                  {formatDateDayjs(
                    checkIsExpiration(inv_inventoryDateLabel)
                      ? task.expirationDate
                      : task.manufactureDate
                  )}
                </Typography>
              </ASTableV2Cell>
              <ASTableV2Cell>
                <Typography variant="tableBody">
                  {task.purchaseOrderNumber || ""}
                </Typography>
              </ASTableV2Cell>
              <ASTableV2Cell>
                <Typography variant="tableBody">
                  {task?.decantingRate
                    ? `${task.decantingRate.value.toString()} ${task.decantingRate.units}`
                    : ""}
                </Typography>
              </ASTableV2Cell>

              <ASTableV2Cell>
                <NotesRow
                  receivingContainerCode={task.receivingContainerCode}
                />
              </ASTableV2Cell>
            </ASTableV2Row>
          ))}
      </ASTableV2Body>
    </ASTableV2>
  );
};
